import { useCallback, useEffect, useRef, useState } from 'react';
import { useFitnessResource } from '@/hooks/useFitnessResource';
import { notifyFitnessChanged } from '@/lib/fitnessChanges';
import { fitnessExerciseNoteKey, readFitnessExerciseNote, saveFitnessExerciseNote } from '@/lib/fitness/fitnessPreferences';

export function useFitnessExerciseNotes(ownerId: string, exerciseName: string, muscle: string) {
  const exerciseKey = fitnessExerciseNoteKey(exerciseName, muscle);
  const scope = JSON.stringify([ownerId, exerciseKey]);
  const latestScope = useRef(scope); latestScope.current = scope;
  const loader = useCallback((userId: string, assertCurrent: () => void) => readFitnessExerciseNote(userId, exerciseKey, assertCurrent), [exerciseKey]);
  const resource = useFitnessResource(loader);
  const { userId: resourceUserId, status: resourceStatus, data: resourceData, capture, refresh } = resource;
  const operation = useRef<{ scope: string; ticket: symbol } | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => { operation.current = null; setPending(null); setFailed(null); }, [scope]);
  const saveNotes = useCallback(async (notes: string) => {
    if (resourceUserId !== ownerId || resourceStatus !== 'ready' || resourceData?.exerciseKey !== exerciseKey || operation.current?.scope === scope) return false;
    const ownerCurrent = capture(), ticket = Symbol();
    operation.current = { scope: scope, ticket }; setPending(scope); setFailed(null);
    const current = () => ownerCurrent() && latestScope.current === scope && operation.current?.ticket === ticket;
    try {
      await saveFitnessExerciseNote(ownerId, exerciseKey, notes, () => { if (!current()) throw new Error('Fitness account changed'); });
      if (!current()) return false;
      notifyFitnessChanged(); return true;
    } catch {
      if (current()) { console.error('[Fitness] exercise note save failed'); setFailed(scope); }
      return false;
    } finally { if (current()) { setPending(null); operation.current = null; } }
  }, [resourceUserId, resourceStatus, resourceData?.exerciseKey, capture, ownerId, exerciseKey, scope]);
  const matches = resourceUserId === ownerId && resourceData?.exerciseKey === exerciseKey;
  const retry = useCallback(() => { setFailed(null); return refresh(); }, [refresh]);
  return {
    notes: matches ? resourceData?.notes ?? '' : '',
    status: resourceUserId !== ownerId ? 'loading' as const : resourceStatus === 'ready' && !matches ? 'loading' as const : resourceStatus,
    busy: pending === scope, error: resourceStatus === 'error' || failed === scope,
    retry, capture: capture, saveNotes, exerciseKey,
  };
}
